import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Row, Col, Form, Card } from 'react-bootstrap';
import { useAddQuizGroupMutation, useUpdateQuizGroupMutation } from "../../slices/fun_stuff/quizGroupsSlice";
import { useLoadQuizGroups } from '../../hooks/useLoadQuizGroups';
import { useSocket } from "../../context/SocketContext";
import { toast } from "react-toastify";
import Loader from "../Loader";
import InviteCard from "./InviteCard";
import JoinGroup from "./JoinGroup";
import GroupChallenges from "./GroupChallenges";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faPlus, faEye, faSquareMinus } from '@fortawesome/free-solid-svg-icons';

const QuizGroups = () => {
    const socket = useSocket();

    const { quizUserInfo, quizGroups } = useSelector((state) => state.quizAuth);


    const [groups, setGroups] = useState([]);
    const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);
    const [showGroup, setShowGroup] = useState({});

    // Show/create challenges/join groups
    const [challengeGroupId, setChallengeGroupId] = useState('');
    const [showJoinGroupForm, setShowJoinGroupForm] = useState(false);
    const [groupKey, setGroupKey] = useState('');
    const [inviteKeyList, setInviteKeyList] = useState([]);
    const [inviteGroupKey, setInviteGroupKey] = useState('');
    const [showInviteKeys, setShowInviteKeys] = useState(false);

    // Create group states/methods
    const [groupName, setGroupName] = useState('');
    const [numInvites, setNumInvites] = useState(0);
    
    const [addQuizGroup, {isLoading: addGroupLoading}] = useAddQuizGroupMutation();

    const { loadGroups } = useLoadQuizGroups();

    const [updateGroup] = useUpdateQuizGroupMutation();

    useEffect(() =>  {
        if (quizGroups?.length > 0) {
            setGroups(quizGroups);
        }
    }, [quizGroups]);

    const checkConnections = () => {
        console.log('Checking connections')
        socket?.emit('check_connections', { groupId: '1edb1a35-2f46-4096-9c16-5869a0abf1eb', groupKey: 'test_group' }, (res) => {
            console.log(res);
        })
    }

    useEffect(() => {
        if (groups?.length) {
            const initialShowGroup = {};
            const initialShowInviteKeys = {};
            groups.forEach((_, i) => {
                initialShowGroup[i] = false;
                initialShowInviteKeys[i] = false;
            });
            setShowGroup(initialShowGroup);
            setShowInviteKeys(initialShowInviteKeys);
        }
    }, [groups]);

    async function submitQuizGroup(e) {
        e.preventDefault();
        if (groupName === '') {
            window.alert("Please enter group name");
            return;
        }

        const invite_keys = await createInvites();

        try {
            const res = await addQuizGroup({
                group_name: groupName.trim(),
                invite_keys,
            }).unwrap();
            toast.success(`${res.group_name} group created`);
            setGroupName('');
            setInviteKeyList(res.invite_keys);
            setInviteGroupKey(res.group_key);
            await loadGroups();
        } catch (error) {
            window.alert(error?.data?.message || error.error);
        }
    }

    async function submitCreateKeys(e) {
        e.preventDefault();
        const newKeys = await createInvites();

        try {
            const res = await updateGroup({
                id: challengeGroupId,
                newKeys,
            }).unwrap();
            toast.success(`New keys created for ${res.group_name}`);
            setGroupName('');
            await loadGroups();
        } catch (error) {
            window.alert(error?.data?.message || error.error);
        }
    }

    async function createInvites() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let inviteKeys = [];
        for (let i = 0; i < numInvites; i++) {
            const randomSegment = () =>
                Array.from({ length: 3 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
            
            inviteKeys.push(`${randomSegment()}-${randomSegment()}-${randomSegment()}`);
        }
        return inviteKeys;
    }

    function handleGroupClick(index) {
        setShowGroup({ ...showGroup, [index]: !showGroup[index] });
    }

    function handleShowKeys(index, groupId) {
        setShowInviteKeys({ ...showInviteKeys, [index]: !showInviteKeys[index] });
        setChallengeGroupId(groupId);
    }

    return (
        (!quizUserInfo || !groups) ? <div></div> : (

        <Card className="quiz-groups-main shadow-sm">


            {quizUserInfo?.is_admin && <Button onClick={() => checkConnections()}>Check Connections</Button>}

            <Card.Header className="card-header-dark p-0 mb-3">
                <h3>My Groups</h3>
            </Card.Header>

            <Card.Body>
                {groups.map((group, index) => (
                    <div key={`${group}-${index}`} className="group-div">
                    <Button className="quiz-group-btn" onClick={() => handleGroupClick(index)}>
                        {group.group_name}  {showGroup[index] ? <FontAwesomeIcon icon={faChevronUp} className="ms-1" /> : <FontAwesomeIcon icon={faChevronDown} className="ms-1" />}
                    </Button>
                    {showGroup[index] && 
                    <>
                    <Row className="d-flex justify-content-between" style={{ width: '85%' }}>

                        <Col xs={12} sm={7} lg={9} className="text-start">
                            <GroupChallenges 
                                quizUserInfo={quizUserInfo}
                                group={group}
                                index={index}
                                showGroup={showGroup}
                                setShowGroup={setShowGroup}
                                challengeGroupId={challengeGroupId}
                                setChallengeGroupId={setChallengeGroupId}
                            />
                        </Col>
                    
                        <Col xs={12} sm={5} lg={3}>
                        <h5>Members</h5>
                        {group.members.map((member, idx) => (
                            <Row key={`${member}-${idx}`}>
                            <Col>
                                {member.username}
                                {member.id === group.commissioner_id && ' - commissioner'}
                            </Col>
                            </Row>
                        ))}
                        {group.commissioner_id === quizUserInfo.id && (
                            <>
                            <Button className="quiz-btn-dark" onClick={() => handleShowKeys(index, group.id)}>
                                {!showInviteKeys[index] ? (
                                <>
                                    <FontAwesomeIcon icon={faEye} className="me-1" /> Show invite keys 
                                </>
                                ) : (
                                <>
                                    <FontAwesomeIcon icon={faSquareMinus} className="me-1" /> Hide invite keys
                                </>
                                )}
                            </Button>
                            {showInviteKeys[index] && (
                                <InviteCard 
                                inviteGroupKey={group.group_key}
                                inviteKeyList={group.invite_keys}
                                />
                            )}
                            {showInviteKeys[index] &&
                            <Form onSubmit={(e) => submitCreateKeys(e)}>
                                <Form.Group controlId='create-invites' className="my-2">
                                    <Form.Label>How many more invites do you need?</Form.Label>
                                    <Form.Control
                                        type='number'
                                        placeholder="Number of invites"
                                        onChange={(e) => setNumInvites(e.target.value)}
                                    />
                                </Form.Group>
                                <Button className="quiz-btn-dark" type="submit">Create Invites</Button>
                            </Form>
                            }
                            </>
                        )}
                        </Col>
                    </Row>
                    </>
                    }
                    </div>
                ))}
            </Card.Body>

            <Card.Footer className={`d-flex justify-content-center pt-4 ${(showCreateGroupForm || showJoinGroupForm) ? 'flex-column align-items-center' : 'gap-4'}`}>
                <JoinGroup 
                    groupKey={groupKey} 
                    setGroupKey={setGroupKey} 
                    showCreateGroupForm={showCreateGroupForm}
                    showJoinGroupForm={showJoinGroupForm}
                    setShowJoinGroupForm={setShowJoinGroupForm}
                />

                {!showJoinGroupForm && <Button className='quiz-btn-dark p-3' onClick={() => setShowCreateGroupForm(!showCreateGroupForm)}>
                    {!showCreateGroupForm ? (
                        <>
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Create New Group 
                        </>
                    ) : (
                        <>
                        Close
                        <FontAwesomeIcon icon={faChevronUp} className="ms-1" />
                        </>
                    )
                    }
                </Button>}
                {showCreateGroupForm && 
                <Form onSubmit={(e) => submitQuizGroup(e)}>
                    <Form.Group controlId='create-group' className="my-2">
                        <Form.Label>Group name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder='Enter group name'
                            onChange={e => setGroupName(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group controlId='number-members' className="my-2">
                        <Form.Label>How many friends do you want to invite?</Form.Label>
                        <Form.Control
                            type='number'
                            placeholder="Number of invites"
                            onChange={(e) => setNumInvites(e.target.value)}
                        />
                    </Form.Group>
                    <div className="d-flex justify-content-center">
                        <Button variant="dark" type="submit">Create Group</Button>
                    </div>
                </Form>}

                {addGroupLoading && <Loader />}

                {inviteKeyList && inviteGroupKey && showCreateGroupForm &&
                <>
                <h5 className="my-3">Share the following invites with your friends. Each invite_key is only good for one friend.</h5>
                <InviteCard 
                    inviteGroupKey={inviteGroupKey}
                    inviteKeyList={inviteKeyList}
                />
                </>
                }
            </Card.Footer>
        </Card>
        )
    );
}

export default QuizGroups